import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2, Save, Plus, X, Wand2, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { TONE_PRESETS } from "@/lib/tonePresets";

const MIN_SAMPLES = 2;
const MAX_SAMPLES = 4;
const SAMPLE_MAX_CHARS = 4000;

const emptyDraft = () => ({
  id: null,
  name: "",
  samples: ["", ""],
  tone_description: "",
});

export default function BrandVoice() {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [mode, setMode] = useState("idle"); // idle | new | edit

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brand-voices");
      setVoices(data.items || []);
    } catch {
      toast.error("Failed to load voices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setDraft(emptyDraft());
    setMode("new");
  };

  const startEdit = (voice) => {
    setDraft({
      id: voice.id,
      name: voice.name,
      samples: voice.samples.length >= MIN_SAMPLES
        ? voice.samples
        : [...voice.samples, ...Array(MIN_SAMPLES - voice.samples.length).fill("")],
      tone_description: voice.tone_description || "",
    });
    setMode("edit");
  };

  const cancelEdit = () => {
    setDraft(emptyDraft());
    setMode("idle");
  };

  const updateSample = (idx, value) => {
    setDraft((d) => ({
      ...d,
      samples: d.samples.map((s, i) => (i === idx ? value.slice(0, SAMPLE_MAX_CHARS) : s)),
    }));
  };
  const addSample = () => {
    setDraft((d) => (d.samples.length < MAX_SAMPLES ? { ...d, samples: [...d.samples, ""] } : d));
  };
  const removeSample = (idx) => {
    setDraft((d) => (d.samples.length > MIN_SAMPLES ? { ...d, samples: d.samples.filter((_, i) => i !== idx) } : d));
  };

  const applyPreset = (preset) => {
    setDraft((d) => ({ ...d, tone_description: preset.tone }));
    toast.success(`${preset.label} tone applied`);
  };

  const validSampleCount = draft.samples.filter((s) => s.trim().length >= 20).length;
  const canSave = draft.name.trim().length > 0 && validSampleCount >= MIN_SAMPLES && !saving;

  const handleSave = async () => {
    const cleaned = draft.samples.map((s) => s.trim()).filter((s) => s.length >= 20);
    if (cleaned.length < MIN_SAMPLES) {
      toast.error(`Add at least ${MIN_SAMPLES} samples of 20+ characters each`);
      return;
    }
    if (!draft.name.trim()) {
      toast.error("Give this voice a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        samples: cleaned,
        tone_description: draft.tone_description.trim() || null,
      };
      if (mode === "edit" && draft.id) {
        await api.patch(`/brand-voices/${draft.id}`, payload);
        toast.success(`"${payload.name}" updated`);
      } else {
        await api.post("/brand-voices", payload);
        toast.success(`"${payload.name}" saved — pick it on the Studio to use it`);
      }
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (voice) => {
    if (!window.confirm(`Delete voice "${voice.name}"? This cannot be undone.`)) return;
    setDeletingId(voice.id);
    try {
      await api.delete(`/brand-voices/${voice.id}`);
      toast.success("Deleted");
      if (draft.id === voice.id) cancelEdit();
      await load();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const isEditing = mode !== "idle";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12 lg:px-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— Your voices</p>
            <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">Brand Voices</h1>
            <p className="mt-3 max-w-2xl text-sm text-neutral-600">
              Create as many voices as you like — personal, work, ghost-client, playful side project.
              Pick one per generation from the Studio.
            </p>
          </div>
          {!isEditing && (
            <Button
              onClick={startNew}
              data-testid="brand-voice-new-btn"
              className="h-12 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
            >
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
              New voice
            </Button>
          )}
        </div>

        {/* Voice list */}
        {!isEditing && (
          loading ? (
            <div className="flex justify-center py-24" data-testid="brand-voice-loading">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
            </div>
          ) : voices.length === 0 ? (
            <div className="border border-dashed border-neutral-300 bg-white p-16 text-center" data-testid="brand-voice-empty">
              <Wand2 className="mx-auto mb-4 h-8 w-8 text-neutral-400" strokeWidth={1.5} />
              <p className="font-serif text-2xl italic text-neutral-500">No voices yet.</p>
              <p className="mt-2 text-sm text-neutral-500">Create your first voice — takes under 2 minutes.</p>
              <Button
                onClick={startNew}
                data-testid="brand-voice-empty-cta"
                className="mt-6 h-12 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Create your first voice
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {voices.map((v) => (
                <div
                  key={v.id}
                  data-testid={`brand-voice-card-${v.id}`}
                  className="border border-neutral-200 bg-white p-6 transition-colors hover:border-black"
                >
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <h3
                      data-testid={`brand-voice-name-${v.id}`}
                      className="font-serif text-2xl font-semibold tracking-tight"
                    >
                      {v.name}
                    </h3>
                    <button
                      type="button"
                      onClick={() => handleDelete(v)}
                      disabled={deletingId === v.id}
                      data-testid={`brand-voice-delete-${v.id}`}
                      className="p-1 text-neutral-500 hover:text-black"
                      aria-label="Delete voice"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  {v.tone_description && (
                    <p className="mb-3 line-clamp-2 text-sm italic text-neutral-600">
                      &ldquo;{v.tone_description}&rdquo;
                    </p>
                  )}
                  <p className="text-xs uppercase tracking-widest text-neutral-500">
                    {v.samples.length} sample{v.samples.length > 1 ? "s" : ""}
                  </p>
                  <div className="mt-5 flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEdit(v)}
                      data-testid={`brand-voice-edit-${v.id}`}
                      className="h-9 rounded-none border-neutral-300 text-xs"
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Editor */}
        {isEditing && (
          <div className="space-y-6" data-testid="brand-voice-editor">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                — {mode === "edit" ? "Editing" : "New voice"}
              </p>
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="brand-voice-cancel-edit"
                className="text-xs text-neutral-500 underline underline-offset-4 hover:text-black"
              >
                Back to list
              </button>
            </div>

            {/* Name */}
            <div className="border border-neutral-200 bg-white p-6">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Voice name</Label>
              <Input
                data-testid="brand-voice-name-input"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, 60) }))}
                placeholder='e.g. "Personal", "Work", "Client X"'
                className="mt-3 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
              />
              <p className="mt-2 text-xs text-neutral-400">{draft.name.length} / 60</p>
            </div>

            {/* Tone presets */}
            <div className="border border-neutral-200 bg-white p-6">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">
                Quick tone presets
              </Label>
              <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                {TONE_PRESETS.map((p) => {
                  const active = draft.tone_description === p.tone;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p)}
                      data-testid={`tone-preset-${p.key}`}
                      className={`border p-3 text-left transition-colors ${
                        active
                          ? "border-coral bg-coral-subtle text-coral"
                          : "border-neutral-200 bg-white hover:border-black"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {active && <Check className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        <span className="font-serif text-lg font-semibold">{p.label}</span>
                      </div>
                      <p className={`mt-1 text-xs ${active ? "text-neutral-300" : "text-neutral-500"}`}>
                        {p.tagline}
                      </p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-widest text-neutral-500">
                  Tone description <span className="text-neutral-300">(optional)</span>
                </Label>
                <Input
                  data-testid="brand-voice-tone-input"
                  value={draft.tone_description}
                  onChange={(e) => setDraft((d) => ({ ...d, tone_description: e.target.value.slice(0, 200) }))}
                  placeholder="Write your own or pick a preset above"
                  className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
                />
                <p className="mt-2 text-xs text-neutral-400">{draft.tone_description.length} / 200</p>
              </div>
            </div>

            {/* Samples */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">— Writing samples</p>
                <span
                  data-testid="brand-voice-sample-count"
                  className={`text-xs ${
                    validSampleCount >= MIN_SAMPLES ? "text-emerald-600" : "text-neutral-500"
                  }`}
                >
                  {validSampleCount} / {MIN_SAMPLES}+ valid
                </span>
              </div>

              {draft.samples.map((s, idx) => {
                const chars = s.length;
                const wordsCount = s.trim() ? s.trim().split(/\s+/).length : 0;
                const isValid = s.trim().length >= 20;
                return (
                  <div
                    key={idx}
                    data-testid={`brand-voice-sample-${idx}`}
                    className="border border-neutral-200 bg-white"
                  >
                    <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-3">
                      <span className="text-xs uppercase tracking-widest text-neutral-500">
                        Sample {idx + 1}
                      </span>
                      {draft.samples.length > MIN_SAMPLES && (
                        <button
                          type="button"
                          onClick={() => removeSample(idx)}
                          data-testid={`brand-voice-remove-${idx}`}
                          className="p-1 text-neutral-400 hover:text-black"
                          aria-label={`Remove sample ${idx + 1}`}
                        >
                          <X className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                    <Textarea
                      data-testid={`brand-voice-sample-input-${idx}`}
                      value={s}
                      onChange={(e) => updateSample(idx, e.target.value)}
                      placeholder="Paste something you've written — a LinkedIn post, tweet, email…"
                      className="min-h-[160px] resize-y rounded-none border-0 bg-transparent p-6 font-serif text-base leading-relaxed placeholder:text-neutral-400 focus-visible:ring-0"
                    />
                    <div className="flex items-center justify-between border-t border-neutral-100 px-6 py-3 text-xs">
                      <span className={isValid ? "text-emerald-600" : "text-neutral-400"}>
                        {isValid ? "✓ ready" : "add at least 20 characters"}
                      </span>
                      <span className="font-mono text-neutral-400">
                        {wordsCount} words · {chars.toLocaleString()} / {SAMPLE_MAX_CHARS.toLocaleString()}
                      </span>
                    </div>
                  </div>
                );
              })}

              {draft.samples.length < MAX_SAMPLES && (
                <button
                  type="button"
                  onClick={addSample}
                  data-testid="brand-voice-add-sample"
                  className="flex w-full items-center justify-center gap-2 border border-dashed border-neutral-300 bg-white/50 py-6 text-sm text-neutral-600 transition-colors hover:border-black hover:text-black"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Add another sample ({draft.samples.length}/{MAX_SAMPLES})
                </button>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-200 pt-8">
              <p className="text-xs text-neutral-500">
                {mode === "edit" ? "Changes apply to all future generations." : "Setup takes under 2 minutes."}
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={cancelEdit}
                  className="h-12 rounded-none border-neutral-300 text-neutral-700"
                  data-testid="brand-voice-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!canSave}
                  data-testid="brand-voice-save-btn"
                  className="h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-50"
                >
                  <Save className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save voice"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
